import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import ThresholdNote from './ThresholdNote';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import FlagSummary from './FlagSummary';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

const money = (n) => `₹${Number(n).toLocaleString()}`;
const dayLabel = (iso) => Number(iso.slice(8, 10));

// One cell per week: the achievement % over "revenue / target". An in-progress or upcoming week is shown greyed with its
// status - only completed weeks are ever evaluated. Red = the completed week is under its target (below 100%, as shown - the
// server rounds the % to 2 decimals); BOLD red = it is also below Finance's warning threshold (a real warning). A week at 100%
// or more keeps the normal colour, and an in-progress / upcoming week is never red (part-week revenue against a full-week
// target would turn nearly everyone red).
const WeekCell = ({ week, hasWeeklyTarget }) => {
  if (!hasWeeklyTarget) return <Typography variant="body2" color="text.secondary" align="right">No target</Typography>;
  const muted = week.status !== 'completed';
  const underTarget = week.status === 'completed' && week.percent !== null && week.percent < 100;
  return (
    <Box textAlign="right" sx={{ opacity: muted ? 0.6 : 1 }}>
      <Typography variant="body2" fontWeight={week.isWarning ? 700 : 500} color={week.isWarning || underTarget ? 'error.main' : 'text.primary'}>
        {week.percent === null ? '-' : `${week.percent}%`}
        {muted && (
          <Typography component="span" variant="caption" color="text.secondary">
            {week.status === 'in_progress' ? ' · in progress' : ' · upcoming'}
          </Typography>
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {money(week.revenue)} / {money(week.target)}
      </Typography>
    </Box>
  );
};

const WeeklyRevenueDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [thresholdNote, setThresholdNote] = useState(null); // why there is no threshold for this month, when there isn't
  const [weeks, setWeeks] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0, thresholdPercent: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/weekly-revenue-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load Weekly Revenue results');
          return;
        }
        setRows(res?.data?.rows || []);
        setThresholdNote(res?.data?.thresholdNote || null);
        setWeeks(res?.data?.weeks || []);
        setSummary({
          totalEvaluated: res?.data?.totalEvaluated || 0,
          warningCount: res?.data?.warningCount || 0,
          thresholdPercent: res?.data?.thresholdPercent ?? null,
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load Weekly Revenue results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  // Most warned weeks first, then lowest completed-week average % first.
  const sortedRows = useMemo(() => [...rows].sort((a, b) => b.warningWeeks - a.warningWeeks), [rows]);

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography
          variant="body2"
          fontStyle={row.isActive === false ? 'italic' : 'normal'}
          color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}
        >
          {row.entityName}
          {row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'monthlyTarget', headerName: 'Monthly Target', align: 'right', render: (row) => money(row.monthlyTarget), csvValue: (row) => row.monthlyTarget },
    ...weeks.flatMap((w, idx) => [
      {
        field: `week${w.week}`,
        headerName: `Week ${w.week} (${dayLabel(w.from)}-${dayLabel(w.to)})`,
        align: 'right',
        render: (row) => <WeekCell week={row.weeks[idx]} hasWeeklyTarget={row.hasWeeklyTarget} />,
        csvValue: (row) => (row.weeks[idx].percent === null ? '' : `${row.weeks[idx].percent}%`),
      },
    ]),
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="Weekly Revenue"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('weekly-revenue-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/weekly-revenue-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Threshold
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated}>
          {summary.thresholdPercent !== null
            ? ` · warns when a completed week is below ${summary.thresholdPercent}% of its target`
            : ' · no threshold configured yet'}
        </FlagSummary>
      )}

      {!loading && !error && <ThresholdNote note={thresholdNote} month={month} configPath="weekly-revenue-config" canSet={canConfigure('weekly-revenue-config')} />}

      {!loading && !error && sortedRows.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Red: a completed week under 100% of its target · bold red: also below the warning threshold
        </Typography>
      )}

      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No one has a Target Revenue set for this period."
          exportFileName="Weekly_Revenue"
        />
      )}
    </WarningPage>
  );
};

export default WeeklyRevenueDashboard;
